import { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare, faCalendarDay, faComment, faPaperclip, faLink,
} from '@fortawesome/free-solid-svg-icons';
import { Client } from '../../../context/ClientContext';
import { useUser } from '../../../context/UserContext';
import { supabase } from '../../../lib/supabase';
import { Avatar, Badge, Button, Card, Dropdown, Icon, Input, SearchableSelect, SidePanel } from '../../ui';
import { formatDate, formatRelativeDate, formatForDateInput, getCurrentDate } from '../../../utils/dateUtils';
import {
  invokeTrelloBoard, labelColor,
  type TrelloCard, type TrelloCardDetail, type TrelloComment, type TrelloList,
} from '../../../lib/trelloBoard';
import TaskDetailSidepanel from './functions/TaskDetailSidepanel';
import type { TaskDetailTask } from './functions/TaskDetailSidepanel';
import { updateTask } from './functions/TasksUtils';

interface BoardRow {
  id: string;
  trello_board_id: string;
  board_name: string;
  client_id: string | null;
}

interface LinkedTask {
  id: string;
  due_date: string | null;
  status: string | null;
  assigned_to_name?: string | null;
  archived?: boolean;
}

interface AttachableTask {
  id: string;
  title: string;
  status: string;
  assigned_to_name: string | null;
  due_date: string | null;
}

interface TrelloSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

export default function TrelloSection({ client, expandedSection }: TrelloSectionProps) {
  const { user } = useUser();
  const [boards, setBoards] = useState<BoardRow[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [assigningId, setAssigningId] = useState<string | null>(null);

  const [lists, setLists] = useState<TrelloList[]>([]);
  const [cards, setCards] = useState<TrelloCard[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openCard, setOpenCard] = useState<TrelloCard | null>(null);
  const [detail, setDetail] = useState<TrelloCardDetail | null>(null);
  const [comments, setComments] = useState<TrelloComment[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Task link — same comms_task_links rows Client Comms uses, keyed by the
  // Trello card id, so an attach here shows up there and vice versa.
  const [linkedTask, setLinkedTask] = useState<LinkedTask | null>(null);
  const [assignees, setAssignees] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [taskDueDate, setTaskDueDate] = useState(() => formatForDateInput(getCurrentDate()));
  const [taskAssignee, setTaskAssignee] = useState<string | null>(null);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [attachableTasks, setAttachableTasks] = useState<AttachableTask[]>([]);
  const [attachTaskId, setAttachTaskId] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [detailTask, setDetailTask] = useState<TaskDetailTask | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

  const assigned = useMemo(
    () => boards.find((b) => b.client_id === client.id) ?? null,
    [boards, client.id],
  );

  const fetchBoards = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('comms_boards')
      .select('id, trello_board_id, board_name, client_id')
      .eq('is_active', true)
      .order('board_name', { ascending: true });
    if (fetchError) {
      console.error('Error loading Trello boards:', fetchError);
      setError('Failed to load boards');
    } else {
      setBoards((data as BoardRow[]) || []);
    }
    setBoardsLoading(false);
  }, []);

  useEffect(() => {
    setBoardsLoading(true);
    setError(null);
    fetchBoards();
  }, [fetchBoards, client.id]);

  useEffect(() => {
    if (!assigned) {
      setLists([]);
      setCards([]);
      return;
    }
    let cancelled = false;
    setBoardLoading(true);
    setError(null);
    invokeTrelloBoard<{ lists: TrelloList[]; cards: TrelloCard[] }>({
      action: 'board',
      boardId: assigned.trello_board_id,
    })
      .then((data) => {
        if (cancelled) return;
        setLists((data.lists || []).slice().sort((a, b) => a.pos - b.pos));
        setCards(data.cards || []);
        setBoardLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading Trello board:', err);
        setError('Failed to load the Trello board');
        setBoardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assigned?.trello_board_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardsByList = useMemo(() => {
    const grouped = new Map<string, TrelloCard[]>();
    for (const card of cards) {
      const bucket = grouped.get(card.idList);
      if (bucket) bucket.push(card);
      else grouped.set(card.idList, [card]);
    }
    for (const bucket of grouped.values()) bucket.sort((a, b) => a.pos - b.pos);
    return grouped;
  }, [cards]);

  const handleAssign = async (row: BoardRow | null) => {
    setAssigningId(row ? row.id : 'none');
    setError(null);
    try {
      // Same semantics as the edit sidepanel: unassign whatever points at this
      // client, then point the chosen board here (steals it if another client had it)
      const { error: unassignError } = await supabase
        .from('comms_boards')
        .update({ client_id: null })
        .eq('client_id', client.id);
      if (unassignError) throw unassignError;

      if (row) {
        const { error: assignError } = await supabase
          .from('comms_boards')
          .update({ client_id: client.id })
          .eq('id', row.id);
        if (assignError) throw assignError;
      }
      await fetchBoards();
    } catch (err) {
      console.error('Error assigning board:', err);
      setError('Failed to update the board assignment');
    } finally {
      setAssigningId(null);
    }
  };

  useEffect(() => {
    supabase
      .from('users')
      .select('id, name, email')
      .order('name')
      .then(({ data, error: usersError }) => {
        if (!usersError) setAssignees((data as Array<{ id: string; name: string; email: string }>) || []);
      });
  }, []);

  // Archived-task links count as no link: the card is back in play, and
  // attach/create upsert over the row. Same rule as Client Comms.
  const refreshLinkedTask = useCallback(async (cardId: string) => {
    const { data, error: linkError } = await supabase
      .from('comms_task_links')
      .select('task:client_tasks(id, due_date, status, assigned_to_name, archived)')
      .eq('card_id', cardId)
      .maybeSingle();
    const task = (data as unknown as { task: LinkedTask | null } | null)?.task;
    setLinkedTask(!linkError && task && !task.archived ? task : null);
  }, []);

  const handleCreateTask = async () => {
    if (!openCard || !taskDueDate || !taskAssignee) return;
    setIsCreatingTask(true);
    try {
      // Anchor to noon local so the date doesn't shift a day across timezones
      const [y, m, d] = taskDueDate.split('-').map(Number);
      const due = new Date(y, m - 1, d, 12).toISOString();

      const { data, error: insertError } = await supabase
        .from('client_tasks')
        .insert({
          client_id: client.id,
          title: openCard.name,
          description: `Created from the Trello panel.\nTrello card: ${openCard.shortUrl}`,
          status: 'Scheduled',
          due_date: due,
          assigned_by: user?.id ?? null,
          assigned_to: taskAssignee,
          assigned_to_name: assignees.find((a) => a.id === taskAssignee)?.name ?? null,
        })
        .select('id, due_date, status, assigned_to_name')
        .single();
      if (insertError) throw insertError;

      // Upsert so a stale link to an archived task gets replaced
      const task = data as LinkedTask;
      const { error: linkError } = await supabase
        .from('comms_task_links')
        .upsert({ card_id: openCard.id, task_id: task.id }, { onConflict: 'card_id' });
      if (linkError) throw linkError;

      setLinkedTask(task);
    } catch (err) {
      console.error('Error creating task from Trello card:', err);
    } finally {
      setIsCreatingTask(false);
    }
  };

  const openAttachPanel = async () => {
    setIsAttachOpen(true);
    setAttachTaskId(null);
    setAttachError(null);
    // Active tasks only — attaching to a Submitted task would instantly
    // clear the card on the comms side
    const { data, error: tasksError } = await supabase
      .from('client_tasks')
      .select('id, title, status, assigned_to_name, due_date')
      .eq('client_id', client.id)
      .eq('archived', false)
      .not('status', 'in', '("Submitted")')
      .order('created_at', { ascending: false })
      .limit(200);
    if (!tasksError) setAttachableTasks((data as AttachableTask[]) || []);
  };

  const handleAttachToTask = async () => {
    if (!openCard || !attachTaskId) return;
    setIsAttaching(true);
    setAttachError(null);
    try {
      const { error: linkError } = await supabase
        .from('comms_task_links')
        .upsert({ card_id: openCard.id, task_id: attachTaskId }, { onConflict: 'card_id' });
      if (linkError) throw linkError;
      setIsAttachOpen(false);
      setAttachTaskId(null);
      await refreshLinkedTask(openCard.id);
    } catch (err) {
      console.error('Error attaching Trello card to task:', err);
      setAttachError('Could not attach — the link was rejected. Try again, and check the console if it persists.');
    } finally {
      setIsAttaching(false);
    }
  };

  const handleDetach = async () => {
    if (!openCard) return;
    const { error: detachError } = await supabase
      .from('comms_task_links')
      .delete()
      .eq('card_id', openCard.id);
    if (!detachError) await refreshLinkedTask(openCard.id);
  };

  const openLinkedTaskDetail = async () => {
    if (!linkedTask) return;
    const { data, error: taskError } = await supabase
      .from('client_tasks')
      .select('*')
      .eq('id', linkedTask.id)
      .single();
    if (!taskError && data) {
      setDetailTask(data as unknown as TaskDetailTask);
      setIsTaskDetailOpen(true);
    }
  };

  const handleTaskDetailUpdate = async (
    taskId: string,
    updated: { title: string; status: string; dueDate: string | null; assignedTo: string | null }
  ) => {
    const { success, error: updateError } = await updateTask(taskId, {
      title: updated.title,
      status: updated.status,
      dueDate: updated.dueDate,
      assignedTo: updated.assignedTo,
    });
    if (!success) {
      console.error('Error updating task from Trello panel:', updateError);
      // Reject so the autosaving drawer reverts the field and toasts.
      throw new Error(updateError || 'Failed to update task');
    }
    // The drawer stays open; it autosaves per field.
    if (openCard) await refreshLinkedTask(openCard.id);
  };

  const handleTaskDetailArchive = async (taskId: string) => {
    const { error: archiveError } = await supabase
      .from('client_tasks')
      .update({ archived: true, archived_at: new Date().toISOString() })
      .eq('id', taskId);
    if (!archiveError) {
      setIsTaskDetailOpen(false);
      if (openCard) await refreshLinkedTask(openCard.id);
    }
  };

  const handleOpenCard = (card: TrelloCard) => {
    setOpenCard(card);
    setDetail(null);
    setComments([]);
    setLinkedTask(null);
    setTaskDueDate(formatForDateInput(getCurrentDate()));
    setTaskAssignee(null);
    refreshLinkedTask(card.id);
    setDetailLoading(true);
    invokeTrelloBoard<{ card: TrelloCardDetail; comments: TrelloComment[] }>({
      action: 'card',
      cardId: card.id,
    })
      .then((data) => {
        setDetail(data.card);
        setComments(data.comments || []);
        setDetailLoading(false);
      })
      .catch((err) => {
        console.error('Error loading Trello card:', err);
        setDetailLoading(false);
      });
  };

  if (expandedSection !== 'trello') return null;

  const spinner = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
      <div
        className="animate-spin w-7 h-7 border-2 rounded-full"
        style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
      />
    </div>
  );

  return (
    <>
      {/* Board explorer */}
      <Card
        eyebrow="Trello"
        title={assigned ? assigned.board_name : 'No board assigned'}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <SearchableSelect
              placeholder="Assign a board..."
              searchPlaceholder="Search boards..."
              clearLabel="No board"
              allowClear
              options={boards.map((row) => ({
                value: row.id,
                label: row.board_name,
                hint: row.client_id !== null && row.client_id !== client.id ? 'In use' : undefined,
              }))}
              value={assigned?.id ?? null}
              onChange={(value) => {
                if (value === (assigned?.id ?? null)) return;
                handleAssign(value ? boards.find((b) => b.id === value) ?? null : null);
              }}
              style={{
                width: '220px',
                ...(assigningId !== null || boardsLoading ? { opacity: 0.6, pointerEvents: 'none' } : {}),
              }}
            />
            {assigned ? (
              <a
                href={`https://trello.com/b/${assigned.trello_board_id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex' }}
              >
                <Button size="sm" variant="secondary">
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '11px', marginRight: '7px' }} />
                  Open in Trello
                </Button>
              </a>
            ) : null}
          </div>
        }
      >
        {error && (
          <div
            style={{
              marginBottom: '14px',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {!assigned && !boardsLoading ? (
          <div style={{ padding: '24px 0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            {boards.length === 0
              ? 'No active boards discovered yet. Boards appear in the picker after the next Trello sync.'
              : 'Pick a Trello board above to explore it here.'}
          </div>
        ) : boardLoading || boardsLoading ? (
          spinner
        ) : lists.length === 0 ? (
          <div style={{ padding: '24px 0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            This board has no open lists.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '6px' }}>
            {lists.map((list) => {
              const listCards = cardsByList.get(list.id) || [];
              return (
                <div
                  key={list.id}
                  style={{
                    width: '232px',
                    flexShrink: 0,
                    background: 'var(--surface-raised)',
                    border: '1px solid var(--border-hairline)',
                    borderRadius: 'var(--radius-md)',
                    padding: '10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: '8px',
                      marginBottom: '10px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                        color: 'var(--text-body)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {list.name}
                    </span>
                    <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                      {listCards.length}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {listCards.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => handleOpenCard(card)}
                        className="pressable"
                        style={{
                          textAlign: 'left',
                          background: 'var(--surface-card)',
                          border: '1px solid var(--border-hairline)',
                          borderRadius: 'var(--radius-md)',
                          padding: '9px 10px',
                          cursor: 'pointer',
                          color: 'var(--text-body)',
                        }}
                      >
                        {card.labels.length > 0 && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
                            {card.labels.map((label) => (
                              <span
                                key={label.id}
                                title={label.name || undefined}
                                style={{
                                  width: '24px',
                                  height: '5px',
                                  borderRadius: '3px',
                                  background: labelColor(label.color),
                                }}
                              />
                            ))}
                          </div>
                        )}
                        <div style={{ fontSize: 'var(--text-sm)', lineHeight: 1.35 }}>{card.name}</div>
                        {(card.due || card.badges?.comments || card.badges?.attachments) ? (
                          <div
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginTop: '6px',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--text-faint)',
                            }}
                          >
                            {card.due && (
                              <span
                                className="t-num"
                                style={card.dueComplete ? { color: 'var(--status-up)' } : undefined}
                              >
                                <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: '10px', marginRight: '4px' }} />
                                {formatDate(card.due, 'MMM d')}
                              </span>
                            )}
                            {card.badges?.comments ? (
                              <span className="t-num">
                                <FontAwesomeIcon icon={faComment} style={{ fontSize: '10px', marginRight: '4px' }} />
                                {card.badges.comments}
                              </span>
                            ) : null}
                            {card.badges?.attachments ? (
                              <span className="t-num">
                                <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: '10px', marginRight: '4px' }} />
                                {card.badges.attachments}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Card detail sidepanel */}
      <SidePanel
        open={openCard !== null}
        onClose={() => setOpenCard(null)}
        eyebrow={detail?.list?.name || 'Trello Card'}
        title={detail?.name || openCard?.name || ''}
        width={460}
        footer={
          <a
            href={detail?.shortUrl || openCard?.shortUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-flex' }}
          >
            <Button variant="secondary">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '11px', marginRight: '7px' }} />
              Open in Trello
            </Button>
          </a>
        }
      >
        {detailLoading ? (
          spinner
        ) : !detail ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            Failed to load this card from Trello.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {(detail.labels.length > 0 || detail.due) && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                {detail.labels.map((label) => (
                  <span
                    key={label.id}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: labelColor(label.color),
                      color: '#fff',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                    }}
                  >
                    {label.name || label.color}
                  </span>
                ))}
                {detail.due && (
                  <Badge tone={detail.dueComplete ? 'up' : 'warn'} dot>
                    Due {formatDate(detail.due, 'MMM d, yyyy')}
                  </Badge>
                )}
              </div>
            )}

            {detail.members && detail.members.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {detail.members.map((member) => (
                  <span key={member.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <Avatar name={member.fullName} size={22} />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{member.fullName}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Task link — attach to an existing task or spin one up */}
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Task</div>
              {linkedTask ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    className="hover-wash pressable"
                    title="Open the linked task"
                    onClick={openLinkedTaskDetail}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      border: '1px solid var(--border-hairline)',
                      background: 'var(--surface-raised)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-body)',
                    }}
                  >
                    <Icon name="check" size={14} style={{ color: 'var(--status-up)', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      Task exists{linkedTask.status ? ` · ${linkedTask.status}` : ''}
                      {linkedTask.assigned_to_name ? ` · ${linkedTask.assigned_to_name}` : ''}
                      {linkedTask.due_date
                        ? ` · due ${new Date(linkedTask.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                        : ''}
                    </span>
                    <Icon name="chevron-right" size={13} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
                  </button>
                  <button
                    type="button"
                    className="hover-wash pressable"
                    title="Detach this card from the task"
                    onClick={handleDetach}
                    style={{
                      display: 'inline-flex',
                      padding: '5px',
                      border: 'none',
                      background: 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      color: 'var(--text-faint)',
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <Input
                      label="Due Date"
                      type="date"
                      value={taskDueDate}
                      onChange={(e) => setTaskDueDate(e.target.value)}
                    />
                    <Dropdown
                      label="Assign To"
                      placeholder="Pick a person..."
                      options={assignees.map((a) => ({ value: a.id, label: a.name }))}
                      value={taskAssignee || ''}
                      onChange={(e) => setTaskAssignee(e.target.value || null)}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <Button size="sm" variant="secondary" icon="link" onClick={openAttachPanel}>
                      Attach
                    </Button>
                    <Button
                      size="sm"
                      icon="plus"
                      disabled={!taskDueDate || !taskAssignee || isCreatingTask}
                      onClick={handleCreateTask}
                    >
                      {isCreatingTask ? 'Creating...' : 'Create Task'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Description</div>
              {detail.desc ? (
                <div
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.5,
                  }}
                >
                  {detail.desc}
                </div>
              ) : (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>No description</div>
              )}
            </div>

            {detail.attachments && detail.attachments.length > 0 && (
              <div>
                <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Attachments</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {detail.attachments.map((att) => (
                    <a
                      key={att.id}
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover-underline"
                      style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--accent-text)',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: '11px', marginRight: '6px' }} />
                      {att.name || att.url}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
                Comments{comments.length > 0 ? ` (${comments.length})` : ''}
              </div>
              {comments.length === 0 ? (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>No comments</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        background: 'var(--surface-raised)',
                        border: '1px solid var(--border-hairline)',
                        borderRadius: 'var(--radius-md)',
                        padding: '8px 10px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '8px',
                          marginBottom: '4px',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-faint)',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                            color: 'var(--text-muted)',
                          }}
                        >
                          {comment.memberCreator?.fullName || 'Unknown'}
                        </span>
                        <span className="t-num">{formatRelativeDate(comment.date)}</span>
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-body)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: 1.45,
                        }}
                      >
                        {comment.data.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              <FontAwesomeIcon icon={faLink} style={{ fontSize: '10px', marginRight: '5px' }} />
              Last activity {formatRelativeDate(detail.dateLastActivity)}
            </div>
          </div>
        )}
      </SidePanel>

      {/* Attach card to an existing task — opens over the card detail */}
      <SidePanel
        open={isAttachOpen}
        onClose={() => setIsAttachOpen(false)}
        eyebrow={openCard ? openCard.name.slice(0, 48) : undefined}
        title="Attach to Task"
        width={420}
        layer={1}
        footer={
          <>
            <Button
              icon="link"
              disabled={!attachTaskId || isAttaching}
              onClick={handleAttachToTask}
            >
              {isAttaching ? 'Attaching...' : 'Attach'}
            </Button>
            <Button variant="ghost" onClick={() => setIsAttachOpen(false)} disabled={isAttaching}>
              Cancel
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {attachError ? (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                color: 'var(--status-down)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {attachError}
            </div>
          ) : null}
          <SearchableSelect
            label="Task"
            placeholder="Pick a task..."
            searchPlaceholder="Search tasks..."
            options={attachableTasks.map((t) => ({
              value: t.id,
              label: t.title,
              hint: `${t.status}${t.assigned_to_name ? ` · ${t.assigned_to_name}` : ''}${
                t.due_date
                  ? ` · due ${new Date(t.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                  : ' · no due date'
              }`,
            }))}
            value={attachTaskId}
            onChange={setAttachTaskId}
          />
          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            Showing this client's active tasks. The card joins the task as a chip on the
            comms side — the task keeps its own name, and submitting it clears the card there.
          </p>
        </div>
      </SidePanel>

      {/* Linked task detail — opens over the card detail */}
      <TaskDetailSidepanel
        isOpen={isTaskDetailOpen}
        onClose={() => setIsTaskDetailOpen(false)}
        selectedTask={detailTask}
        users={assignees}
        onUpdateTask={handleTaskDetailUpdate}
        onArchiveTask={handleTaskDetailArchive}
      />
    </>
  );
}
